import React, {
  FC,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useLocalStorage } from 'react-use'

import { useFlag, useGetNewNotificationsCount } from '@sketch/modules-common'

// Constants
import { localStorageKeys } from '@sketch/constants'

// Utils
import { useCanUseDesignSystemManager } from 'modules/designSystems/operations/useCanUseDesignSystemManager'
import { useIsProfileEnabled } from 'modules/community/hooks'
import {
  canUserAdministerShares as getCanUserAdministerShares,
  showWorkspaceSettings as getShowWorkspaceSettings,
} from 'modules/workspace/utils'

// Components
import * as Collapsible from '@radix-ui/react-collapsible'
import { Box, UnstyledList, ModalContext } from '@sketch/components'
import UserNavbarSection from 'modules/user/components/UserNavbarSection'
import { EmptyTrashModal } from 'modules/shares/components/EmptyTrashModal'
import { ProjectsMenu } from 'modules/projects/components/ProjectsMenu'
import { useShareDeleteWithUi } from 'modules/shares/components/DeleteShareModal'
import { DesignSystemSidebar } from 'modules/designSystems/components'

// Local Components
import { WorkspacesDropdown } from '../../containers/WorkspacesDropdown/WorkspacesDropdown'
import { MenuItem, getMenuItems, getWorkspaceMenuItems } from './menuItems'
import BottomMenu from './components/BottomMenu/BottomMenu'

// Styles
import {
  SidebarWrapper,
  WorkspaceSelectorWrapper,
  MenuWrapper,
  Header,
  FooterWrapper,
  CollapsibleIcon,
  MenuListItem,
} from './Sidebar.styles'

// GQL
import {
  GetProjectsQuery,
  useGetInitialUserQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { WorkspaceSidebarNotice } from '../WorkspaceSidebarNotice'

interface SidebarProps {
  workspace: WorkspaceMinimalFragment
}

// Helper Components
const renderMenuItems = (menuItems: MenuItem[]): ReactNode => (
  <UnstyledList role={!menuItems.length ? undefined : 'menu'}>
    {menuItems.map(({ key, item, hidden }) =>
      !hidden ? <MenuListItem key={key}>{item}</MenuListItem> : null
    )}
  </UnstyledList>
)

export const Sidebar: FC<SidebarProps> = ({ workspace }) => {
  const { showModal } = useContext(ModalContext)

  /**
   * Sidebar FF clean up
   * https://github.com/sketch-hq/Cloud/issues/15634
   */
  const isSidebarFFOn = useFlag('sidebar')
  const hasWorkspaceTemplatesEnabled = useFlag('set-as-template')
  const hasGuestsInProjects = useFlag('guests-in-project')
  const isDesignSystemsOn = useFlag('design-systems')

  const { isProfileEnabled } = useIsProfileEnabled(workspace.identifier)

  const { data: userData } = useGetInitialUserQuery()

  const { canUseDesignSystemManager } = useCanUseDesignSystemManager({
    workspace,
  })

  const localStorageKey = localStorageKeys.sidebarSectionDocumentsCollapsed
  const dsmIntroductionSeen = localStorageKeys.dsmIntroductionSeen
  const [
    isCollapsedDocumentsSection,
    setIsCollapsedDocumentsSection,
  ] = useLocalStorage(localStorageKey)

  const [introductionSeen, setIntroductionSeen] = useLocalStorage(
    dsmIntroductionSeen
  )

  const canUserAdministerShares = getCanUserAdministerShares(workspace)
  const showWorkspaceSettings = getShowWorkspaceSettings(workspace)
  const newNotificationsCount = useGetNewNotificationsCount()

  //   Projects
  const isFinance = workspace.userRole === 'FINANCE'
  const isGuest = workspace.userRole === 'GUEST'
  const isPartner = userData?.me.isPartner
  const isWorkspaceActive = workspace.status === 'ACTIVE'

  // if Sidebar FF is ON, we show the projects else, we check the workspace status
  const canShowProjectsForInactiveWorkspaces =
    isSidebarFFOn || isWorkspaceActive

  const showProjectsList =
    userData &&
    canShowProjectsForInactiveWorkspaces &&
    !isFinance &&
    !isPartner &&
    // only show the projects list for guests with the `guests-in-project`
    // feature flag enabled
    (!isGuest || hasGuestsInProjects)

  const [hasDraftsProject, setHasDraftsProject] = useState(false)

  const handleProjectsLoad = (data: GetProjectsQuery) => {
    const draftsProject = data?.workspace.draftsProject.entries[0]
    setHasDraftsProject(Boolean(draftsProject))
  }

  // Delete share
  const [deleteShare] = useShareDeleteWithUi()

  const handleOnEmptyTrash = () => {
    showModal(EmptyTrashModal, {
      workspaceId: workspace.identifier,
    })
  }

  // Items
  const menuItems = getMenuItems({
    workspaceId: workspace.identifier,
    workspaceName: workspace.name,
    showWorkspaceSettings: !isSidebarFFOn && showWorkspaceSettings,
    showUnseenNotificationBadge: newNotificationsCount > 0,
    showUpdatesLink: !isSidebarFFOn,
    showHelpLink: !isSidebarFFOn,
    showDiscoverLink: !isSidebarFFOn,
    userRole: workspace.userRole,
    userCanAdminister: workspace.userCanAdminister,
  })

  const workspaceMenuItems = getWorkspaceMenuItems({
    workspaceId: workspace.identifier,
    canUseDesignSystemManager,
    userRole: workspace.userRole,
    hasDraftsProject,
    hasWorkspaceTemplatesEnabled,
    canUserAdministerShares,
    handleOnEmptyTrash,
    deleteShare,
    isProfileEnabled,
  })

  const hasVisibleMenuitems = !workspaceMenuItems.every(item => item.hidden)

  const handleCollapsibleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsCollapsedDocumentsSection(!isCollapsedDocumentsSection)
  }

  // Handle DSM upsell banner
  const handleScroll = useCallback(() => {
    if (introductionSeen) return

    setIntroductionSeen(true)
    window.dispatchEvent(new Event('dsm_intro_seen'))
  }, [setIntroductionSeen, introductionSeen])

  useEffect(() => {
    if (introductionSeen) return

    window.addEventListener('scroll', handleScroll, { capture: true })

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [introductionSeen, handleScroll])

  return (
    <SidebarWrapper>
      {workspace && (
        <WorkspaceSelectorWrapper>
          <WorkspacesDropdown workspace={workspace} />
        </WorkspaceSelectorWrapper>
      )}
      <WorkspaceSidebarNotice workspace={workspace} />
      <MenuWrapper>
        {renderMenuItems(menuItems)}

        {!isPartner && !isSidebarFFOn && (
          <>
            <Header>Workspace</Header>
            {renderMenuItems(workspaceMenuItems)}
          </>
        )}

        {!isPartner && isSidebarFFOn && hasVisibleMenuitems && (
          <>
            <Collapsible.Root
              data-testid="documents"
              className="CollapsibleRoot"
              open={!isCollapsedDocumentsSection}
              onOpenChange={
                setIsCollapsedDocumentsSection as (isOpen: boolean) => void
              }
            >
              {/** * Sidebar FF clean up *
              https://github.com/sketch-hq/Cloud/issues/15634 */}
              <Header isSidebarFFOn>
                Documents
                <Collapsible.Trigger
                  data-testid="collapse-documents-button"
                  asChild
                >
                  <CollapsibleIcon
                    isOpen={!isCollapsedDocumentsSection}
                    onClick={handleCollapsibleClick}
                  />
                </Collapsible.Trigger>
              </Header>
              {!isCollapsedDocumentsSection ? (
                <Collapsible.Content data-testid="documents-content">
                  {renderMenuItems(workspaceMenuItems)}
                </Collapsible.Content>
              ) : (
                <>
                  {renderMenuItems(
                    workspaceMenuItems.filter((item: MenuItem) => item.isActive)
                  )}
                </>
              )}
            </Collapsible.Root>
          </>
        )}

        {(!isDesignSystemsOn || !canUseDesignSystemManager) && <Box mb={4} />}

        {isDesignSystemsOn && canUseDesignSystemManager && (
          <DesignSystemSidebar workspace={workspace} />
        )}

        {showProjectsList && (
          <ProjectsMenu
            workspace={workspace}
            canUserAdministerShares={canUserAdministerShares}
            onLoadCompleted={handleProjectsLoad}
          />
        )}

        {isDesignSystemsOn && !canUseDesignSystemManager && (
          <DesignSystemSidebar workspace={workspace} />
        )}
      </MenuWrapper>
      <FooterWrapper>
        {isSidebarFFOn && <BottomMenu workspace={workspace} />}
        <UserNavbarSection />
      </FooterWrapper>
    </SidebarWrapper>
  )
}
